import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import type { User } from "@/api";
import { query } from "@/queries";

function userQueryOptions() {
  return query.user.me();
}

/** Authed layout guard: cached user returns immediately; cold cache awaits /me. */
export async function requireAuthedUser(
  queryClient: QueryClient
): Promise<{ user: User }> {
  const opts = userQueryOptions();
  const cached = queryClient.getQueryData<User | null>(opts.queryKey);

  if (cached) {
    // fetchQuery is a no-op on the network while the entry is still fresh.
    void queryClient.fetchQuery(opts);
    return { user: cached };
  }
  if (cached === null) throw redirect({ to: "/login" });

  const user = await queryClient.fetchQuery(opts);
  if (!user) throw redirect({ to: "/login" });
  return { user };
}

/** Login route guard: bounce visitors who already have a session. */
export async function redirectIfAuthed(queryClient: QueryClient) {
  const opts = userQueryOptions();
  const cached = queryClient.getQueryData<User | null>(opts.queryKey);
  if (cached) throw redirect({ to: "/" });
  if (cached === null) return;

  const user = await queryClient.fetchQuery(opts);
  if (user) throw redirect({ to: "/" });
}
